<template>
  <HandshakeSheet v-if="visible" cover @close="dismiss">
    <template #cover>
      <HandshakeCodePanel @done="dismiss">
        <!-- The clock is already the game, so only the profile offers it. -->
        <template v-if="!fromClock" #beside-done>
          <button
            type="button"
            class="recommend"
            :aria-label="localize('handshake.game.openSuggestion')"
            @click="openClock"
          >
            960
          </button>
        </template>
      </HandshakeCodePanel>
    </template>
  </HandshakeSheet>
</template>

<script setup lang="ts">
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import { localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import { pushHandshakeClock } from '@/views/main/clock/utils/open-handshake-clock';
import HandshakeCodePanel from '@/views/main/handshakes/HandshakeCodePanel.vue';
import HandshakeSheet from '@/views/main/handshakes/HandshakeSheet.vue';
import { provideHandshakeCode } from '@/views/main/handshakes/use-handshake-code';
import { passerbyDisplayName } from '@/views/main/handshakes/utils/handshake-game';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

const props = defineProps<{
  /**
   * Opened from a clock that already has a game. A passerby is shown straight
   * away. A member handshake waits here, the way a profile one does, so the
   * code stays where the other person can type it.
   */
  fromClock?: boolean;
  /** The clock's game, written onto the handshake so both draw the same one. */
  suggestionSeed?: string;
  suggestionRound?: number;
}>();

const emit = defineEmits<{
  close: [];
  'visible-change': [visible: boolean];
  'handshake-created': [handshake: HandshakeDto, existing?: boolean];
}>();

const authStore = useAuthStore();
const { user, userDetails } = storeToRefs(authStore);

const visible = ref(false);

const code = provideHandshakeCode({
  fromClock: () => !!props.fromClock,
  suggestionSeed: () => props.suggestionSeed,
  suggestionRound: () => props.suggestionRound,
  retainPasserby: () => !props.fromClock,
  onHandshake: (handshake, existing) => emit('handshake-created', handshake, existing),
  onLeave: () => handleClose(),
});

/** The passerby just made, on the clock. The code stays on the handshake for whoever types it. */
function openClock() {
  const handshake = code.passerbyHandshake;
  const id = user.value?.id;
  // The sign-in token has no username on it; the profile does.
  const username = userDetails.value?.username;
  if (!handshake || !id || !username) return;

  leaveForClock();
  pushHandshakeClock({
    handshakeId: handshake.id,
    players: [
      { id, username, role: 'initiator' },
      {
        id: `${handshake.id}:passerby`,
        username: passerbyDisplayName(localize('handshake.player.passerby'), handshake.passerbyClaimCode, true),
        role: 'responder',
        isPasserby: true,
      },
    ],
    round: handshake.suggestionRound ?? 0,
    suggestionSeed: handshake.suggestionSeed,
    initiatorPlaysWhite: handshake.suggestionInitiatorWhite,
    minutes: handshake.suggestionMinutes,
    increment: handshake.suggestionIncrement,
    clockChosen: handshake.suggestionClockChosen,
  });
}

/** The clock has the game, so the code sheet is not waiting behind it. */
function leaveForClock() {
  const wasVisible = visible.value;
  visible.value = false;
  code.reset();
  if (!wasVisible) return;

  emit('visible-change', false);
}

/**
 * Tapping off the sheet. A code someone is still typing is kept, so opening
 * the sheet again shows that same one until it expires. The dialog also
 * reports this as it goes, and that second report has nothing left to do.
 */
function dismiss() {
  const keep = !!code.waitingForUsername && !!code.code;
  const wasVisible = visible.value;
  visible.value = false;
  if (!keep) code.reset();
  if (!wasVisible) return;

  emit('visible-change', false);
  emit('close');
}

function handleClose() {
  const wasVisible = visible.value;
  visible.value = false;
  code.reset();
  if (!wasVisible) return;

  emit('visible-change', false);
  emit('close');
}

/**
 * A fresh code. Coming back to one that is already waiting does not cut
 * another: the other person is typing the one still on the sheet.
 */
function show() {
  if (code.waitingForUsername && code.code) {
    resume();
    return;
  }

  code.reset();
  visible.value = true;
  void code.generate();
  emit('visible-change', true);
}

/** The sheet again, on the code that is already waiting. A new one, when nothing is. */
function resume() {
  if (!(code.waitingForUsername && code.code)) {
    show();
    return;
  }

  visible.value = true;
  code.inputCode = '';
  code.inputError = false;
  code.resolving = false;
  emit('visible-change', true);
}

/** The passerby just cut from PLAY. The code is already made, and closing this sheet leaves the clock. */
function showPasserby(handshake: HandshakeDto) {
  if (!handshake.passerbyClaimCode) return;

  code.reset();
  code.applyPasserby(handshake);
  visible.value = true;
  emit('visible-change', true);
}

defineExpose({ show, resume, close: handleClose, showPasserby });
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

/* The clock's 960 disc while the game is still to play: the next step, so it stands out from the X. */
.recommend {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: var(--color-white);
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  cursor: pointer;
  touch-action: manipulation;
  @include raised-action;
  @include press;
}
</style>
